/**
 * ViewingModeSelector - Home Page Mode Selection Component
 *
 * Minimal, visible selector for viewing mode preference on the home page.
 * Shows before user enters text for analysis.
 *
 * Design:
 * - Horizontal pill-style buttons with emoji indicators
 * - Compact, single-row layout
 * - Y2K styling consistent with app theme
 * - Selected state with gradient background
 *
 * Accessibility:
 * - role="radiogroup" for screen readers
 * - Clear labels for each mode
 * - Keyboard navigation support
 *
 * Usage:
 * ```tsx
 * import { ViewingModeSelector } from '@/components/theatrical/ViewingModeSelector';
 *
 * function HomeScreen() {
 *   const { variant, setMode } = useTheatricalMode();
 *
 *   return (
 *     <View>
 *       <Text>Choose your experience:</Text>
 *       <ViewingModeSelector
 *         selectedMode={variant}
 *         onSelectMode={setMode}
 *       />
 *     </View>
 *   );
 * }
 * ```
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet, TextStyle } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { createTextShadow } from '@/lib/design-tokens';
import type { TheatricalModeVariant } from '@/lib/theatrical/theatricalModeConfig';
import { useEntitlements } from '@/hooks/useEntitlements';

/**
 * Props for ViewingModeSelector
 */
export interface ViewingModeSelectorProps {
  /** Currently selected mode */
  selectedMode: TheatricalModeVariant;

  /** Callback when user selects a mode */
  onSelectMode: (mode: TheatricalModeVariant, source?: 'url_parameter' | 'manual_toggle' | 'adaptive_default') => Promise<void>;

  /** Optional compact mode for smaller screens */
  compact?: boolean;
}

/**
 * Mode display configuration
 *
 * Tab Order (per product requirements 2026-02-03):
 * 1. Enjoy the Show - Available for ALL users (anonymous, free, paid)
 * 2. Skip to Results - Paid users only (drama_plus, dramax)
 * 3. VIP Experience - DramaX only
 */
const MODE_OPTIONS: Array<{
  variant: TheatricalModeVariant;
  emoji: string;
  label: string;
  shortLabel?: string;
  tierRestriction?: 'paid' | 'dramax';
}> = [
  {
    variant: 'theatrical',
    emoji: '🎭',
    label: 'Enjoy the Show',
    shortLabel: 'Show',
    // Available for ALL users (no restriction)
  },
  {
    variant: 'quick',
    emoji: '⚡',
    label: 'Skip to Results',
    shortLabel: 'Skip',
    tierRestriction: 'paid', // drama_plus or dramax only
  },
  {
    variant: 'dramax_premium',
    emoji: '👑',
    label: 'VIP Experience',
    shortLabel: 'VIP',
    tierRestriction: 'dramax',
  },
];

/**
 * ViewingModeSelector Component
 *
 * Horizontal selector for choosing viewing mode on home page.
 */
export function ViewingModeSelector({
  selectedMode,
  onSelectMode,
  compact = false,
}: ViewingModeSelectorProps) {
  const { tier } = useEntitlements();

  // Determine if user is paid (drama_plus or dramax)
  const isPaidUser = tier === 'drama_plus' || tier === 'dramax';
  const isDramaxUser = tier === 'dramax';

  const handleSelect = async (mode: TheatricalModeVariant) => {
    await onSelectMode(mode, 'manual_toggle');
  };

  return (
    <View
      style={styles.container}
      accessibilityRole="radiogroup"
      accessibilityLabel="Choose your viewing experience"
    >
      {MODE_OPTIONS.map((option) => {
        const isSelected = selectedMode === option.variant;
        // Tier restriction logic:
        // - 'paid' = requires drama_plus or dramax
        // - 'dramax' = requires dramax only
        // - undefined = available for all
        const isLocked =
          (option.tierRestriction === 'paid' && !isPaidUser) ||
          (option.tierRestriction === 'dramax' && !isDramaxUser);
        const isDisabled = isLocked;

        return (
          <Pressable
            key={option.variant}
            onPress={() => !isDisabled && handleSelect(option.variant)}
            disabled={isDisabled}
            style={({ pressed }) => [
              styles.modeButton,
              isSelected && styles.modeButtonSelected,
              isDisabled && styles.modeButtonDisabled,
              pressed && !isDisabled && styles.modeButtonPressed,
            ]}
            accessibilityRole="radio"
            accessibilityState={{ checked: isSelected, disabled: isDisabled }}
            accessibilityLabel={`${option.label}${isLocked ? (option.tierRestriction === 'dramax' ? ' (DramaX exclusive)' : ' (Premium feature)') : ''}`}
          >
            {isSelected ? (
              <LinearGradient
                colors={['#A78BFA', '#60A5FA']} // DD-261: purple-400 → blue-400
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.selectedGradient}
              >
                <View style={styles.modeContent}>
                  <Text style={styles.modeEmoji}>{option.emoji}</Text>
                  <Text style={[styles.modeLabel, styles.modeLabelSelected]}>
                    {compact && option.shortLabel ? option.shortLabel : option.label}
                  </Text>
                </View>
              </LinearGradient>
            ) : (
              <View style={styles.modeContent}>
                <Text style={[styles.modeEmoji, isDisabled && styles.modeEmojiDisabled]}>
                  {option.emoji}
                </Text>
                <Text style={[styles.modeLabel, isDisabled && styles.modeLabelDisabled]}>
                  {compact && option.shortLabel ? option.shortLabel : option.label}
                  {isLocked && ' 🔒'}
                </Text>
              </View>
            )}
          </Pressable>
        );
      })}
    </View>
  );
}

// Platform-aware text shadow for selected label
const modeLabelSelectedShadow = createTextShadow({
  color: 'rgba(0, 0, 0, 0.3)',
  offsetX: 0,
  offsetY: 1,
  blurRadius: 2,
});

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  modeButton: {
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)', // DD-261: border-white/10
    backgroundColor: 'rgba(255, 255, 255, 0.05)', // DD-261: bg-white/5
    overflow: 'hidden',
    minWidth: 100,
    minHeight: 44, // Phase 3: WCAG AAA touch targets
  },
  modeButtonSelected: {
    borderColor: 'transparent',
    backgroundColor: 'transparent',
  },
  modeButtonDisabled: {
    opacity: 0.5,
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  modeButtonPressed: {
    transform: [{ scale: 1.05 }], // DD-261: Hover scale effect from HTML prototype
  },
  selectedGradient: {
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  modeContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 14,
    paddingVertical: 10,
  },
  modeEmoji: {
    fontSize: 16,
    lineHeight: 16,
  },
  modeEmojiDisabled: {
    opacity: 0.5,
  },
  modeLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: 'rgba(255, 255, 255, 0.85)',
    letterSpacing: 0.3,
  },
  modeLabelSelected: {
    color: '#FFFFFF',
    ...modeLabelSelectedShadow,
  } as TextStyle,
  modeLabelDisabled: {
    color: 'rgba(255, 255, 255, 0.4)',
  },
});
